import {
  ROUTE_PATTERNS,
  isLocale,
  publicLocalePath,
  workspaceSectionPath,
} from "@/constants/routes";
import { DEFAULT_LOCALE } from "@/constants/config";
import { browserLanguages } from "@/lib/preferences";
import { matchBrowserLocale } from "@bizy/core/i18n/resolve";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { LoadingState, ErrorState } from "@/components/shared/states";
import { Button } from "@/components/ui/button";
import { NotFound } from "@/routes/not-found";
import { Landing } from "@/routes/public/landing";
import { WorkspaceLayout } from "@/routes/workspace/layout";
import { WorkspaceHome } from "@/routes/workspace/home";
import { PosPage } from "@/routes/workspace/pos";
import { OrdersPage } from "@/routes/workspace/orders";
import { OrderDetailPage } from "@/routes/workspace/order-detail";
import { ProductsPage } from "@/routes/workspace/products";
import { LogsPage } from "@/routes/workspace/logs";
import { UsersPage } from "@/routes/workspace/users";
import { SettingsPage } from "@/routes/workspace/settings";
import { InvoicePrintPage } from "@/routes/workspace/invoice-print";
import { useDemoWorkspace } from "@/hooks/use-demo-workspace";

/**
 * Unlocalized public requests are redirected to the browser's preferred locale,
 * falling back to the default (agents.md §4).
 */
const LocaleLanding = () => {
  const { lang } = useParams();
  if (!lang || !isLocale(lang)) {
    return <Navigate to={publicLocalePath(matchBrowserLocale(browserLanguages()))} replace />;
  }
  return <Landing locale={lang} />;
};

const RootRedirect = () => (
  <Navigate to={publicLocalePath(matchBrowserLocale(browserLanguages()) || DEFAULT_LOCALE)} replace />
);

/**
 * `/dashboard` no longer shows a workspace selector (PRD §2.2 assumes an
 * authenticated account with several workspaces). In the preview build it resolves
 * the demo workspace and lands on its dashboard — same target as the navbar CTA.
 */
const DashboardRedirect = () => {
  const demo = useDemoWorkspace();

  if (demo.isLoading) return <LoadingState label="Opening the demo workspace…" />;

  if (demo.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-3">
          <ErrorState
            title="Could not reach the API"
            description="The dashboard needs the API (and its Supabase schema). Check /api/v1/health for the exact reason."
          />
          <div className="flex justify-center">
            <Button asChild variant="outline" size="sm">
              <a href="/api/v1/health" target="_blank" rel="noreferrer noopener">
                Check API health
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!demo.slug) {
    return <Navigate to={publicLocalePath(DEFAULT_LOCALE)} replace />;
  }
  return <Navigate to={workspaceSectionPath(demo.slug, "home")} replace />;
};

export const router = createBrowserRouter([
  { path: "/", element: <RootRedirect /> },
  { path: ROUTE_PATTERNS.publicLocale, element: <LocaleLanding /> },
  { path: ROUTE_PATTERNS.publicLocale + "/*", element: <LocaleLanding /> },
  { path: ROUTE_PATTERNS.dashboard, element: <DashboardRedirect /> },
  { path: ROUTE_PATTERNS.dashboardNewWorkspace, element: <DashboardRedirect /> },
  { path: ROUTE_PATTERNS.login, element: <DashboardRedirect /> },
  { path: ROUTE_PATTERNS.workspaceInvoicePrint, element: <InvoicePrintPage /> },
  {
    path: ROUTE_PATTERNS.workspaceRoot,
    element: <WorkspaceLayout />,
    children: [
      { index: true, element: <WorkspaceHome /> },
      { path: "pos", element: <PosPage /> },
      { path: "orders", element: <OrdersPage /> },
      { path: "orders/:orderId", element: <OrderDetailPage /> },
      { path: "products", element: <ProductsPage /> },
      { path: "logs", element: <LogsPage /> },
      { path: "users", element: <UsersPage /> },
      { path: "settings", element: <SettingsPage /> },
    ],
  },
  { path: ROUTE_PATTERNS.superboard, element: <DashboardRedirect /> },
  { path: ROUTE_PATTERNS.superboardLogin, element: <DashboardRedirect /> },
  { path: ROUTE_PATTERNS.notFound, element: <NotFound /> },
  { path: "*", element: <NotFound /> },
]);
